// frontend/src/components/VitalsAddModal.js
// Modal component for adding vitals data directly in the flowsheet

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Input,
  Space,
  Typography,
  Alert,
  message
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import MomentDatePicker from './MomentDatePicker.tsx';
import axios from 'axios';
import { usePatientChart } from '../context/PatientChartContext';

const { Title, Text } = Typography;

/**
 * VitalsAddModal Component
 * Provides a dedicated interface for adding vitals data to the flowsheet
 */
const VitalsAddModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment());
  const [hasFormData, setHasFormData] = useState(false);
  const { refreshVitals } = usePatientChart();

  // Check if form has any data
  const checkFormData = () => {
    const values = form.getFieldsValue();
    const hasData = Object.values(values).some(value => 
      value !== null && value !== undefined && value !== ''
    );
    setHasFormData(hasData);
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedDateTime(moment());
      setHasFormData(false);
    }
  }, [visible, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form fields
      const values = await form.validateFields();
      
      // Filter out empty values
      const filteredValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          filteredValues[key] = values[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one vital sign value.');
        return;
      }

      // Prepare payload for backend
      const payload = {
        entryDate: selectedDateTime.toISOString(),
        vitals: filteredValues
      };

      // Call backend API
      const response = await axios.post(`/patients/${patientId}/flowsheet/vitals`, payload);

      if (response.data.status === 'success') {
        message.success('Vitals data saved successfully!');

        // Refresh vitals banner
        refreshVitals();

        // Call parent onSave callback to refresh flowsheet
        if (onSave) {
          onSave();
        }

        // Close modal and reset form
        onClose();
        form.resetFields();
      } else {
        throw new Error(response.data.message || 'Failed to save vitals data');
      }
    } catch (error) {
      console.error('Error saving vitals data:', error);
      message.error(error.response?.data?.message || 'Failed to save vitals data. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    setSelectedDateTime(moment());
    setHasFormData(false);
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined />
          <Title level={4} style={{ margin: 0 }}>
            Add Vitals Data
          </Title>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={800}
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={submitLoading}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={submitLoading}
          disabled={!hasFormData}
        >
          Save Vitals
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Add New Vitals Entry"
          description="Enter vital signs data for the selected date and time. Only non-calculated parameters can be entered directly."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Date/Time Selection */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Text strong>Entry Date & Time:</Text>
            <br />
            <MomentDatePicker
              showTime
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select date and time"
            />
          </Col>
        </Row>
      </div>

      <Form form={form} layout="vertical" name="vitals_add_form" onValuesChange={checkFormData}>
        <Row gutter={16}>
          {/* Blood Pressure */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item label="Blood Pressure (mmHg)">
              <Input.Group compact>
                <Form.Item name="vitals_sbp" noStyle>
                  <InputNumber 
                    style={{ width: '50%' }} 
                    placeholder="Systolic"
                    min={50}
                    max={300}
                    precision={0}
                  />
                </Form.Item>
                <Form.Item name="vitals_dbp" noStyle>
                  <InputNumber 
                    style={{ width: '50%' }} 
                    placeholder="Diastolic"
                    min={30}
                    max={200}
                    precision={0}
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>
          </Col>

          {/* Heart Rate */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="vitals_rhr" label="Heart Rate (bpm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter HR"
                min={30}
                max={220}
                precision={0}
              />
            </Form.Item>
          </Col>

          {/* Respiratory Rate */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="vitals_rr" label="Respiratory Rate (breaths/min)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter RR"
                min={8}
                max={60}
                precision={0}
              />
            </Form.Item>
          </Col>

          {/* Oxygen Saturation */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="vitals_spo2" label="Oxygen Saturation (%)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter SpO2"
                min={70}
                max={100}
                precision={0}
              />
            </Form.Item>
          </Col>

          {/* Temperature */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="vitals_temp_c" label="Temperature (°C)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter temp"
                min={30}
                max={45}
                step={0.1}
              />
            </Form.Item>
          </Col>

          {/* Weight */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="vitals_weight" label="Weight (kg)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter weight"
                min={1}
                max={500}
                step={0.1}
              />
            </Form.Item>
          </Col>

          {/* Height */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="vitals_height" label="Height (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter height"
                min={50}
                max={250}
                step={0.1}
              />
            </Form.Item>
          </Col>

          {/* Pain Level */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="vitals_pain" label="Pain Level (0-10)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter pain"
                min={0}
                max={10}
                precision={0}
              />
            </Form.Item>
          </Col>


        </Row>
      </Form>

      <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
        <Text type="secondary" style={{ fontSize: '0.9em' }}>
          <strong>Note:</strong> Calculated parameters (MAP, Pulse Pressure, BMI, SIRS Score, HRV, VO₂ Max) will be automatically computed from the entered values.
        </Text>
      </div>
    </Modal>
  );
};

export default VitalsAddModal;
